/**
 * Reactive store for tracking background generation after a stream interruption.
 *
 * When the SSE connection drops without a clean `finish` event, the server
 * continues generating in the background.  This store manages the polling
 * lifecycle: detecting completion, surfacing errors, and triggering a
 * thread reload once the response is ready.
 *
 * UI components subscribe via useSyncExternalStore to show inline indicators.
 */

import type { ChatOut } from "../types/cuadra";

type Listener = () => void;

export type BackgroundGenerationStatus =
  | "polling"
  | "completed"
  | "error"
  | "timeout";

export interface BackgroundGenerationState {
  chatId: string;
  status: BackgroundGenerationStatus;
  startedAt: number;
}

const POLL_INTERVALS_MS = [1000, 2000, 4000, 8000, 16000, 30000];
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

class BackgroundGenerationStore {
  private current: BackgroundGenerationState | null = null;
  private listeners = new Set<Listener>();
  private pollAbortController: AbortController | null = null;
  private _reloadHandler: ((chatId: string) => void) | null = null;
  /** Captured from `startPolling` so completion can run `onStreamEnd` even if `poll()` is aborted. */
  private pendingOnStreamEnd: ((chatId: string) => void) | null = null;

  /**
   * Register a callback invoked when polling detects that background
   * generation completed.  The consuming runtime provider uses this
   * to reload the thread with the new messages.
   */
  setReloadHandler(handler: ((chatId: string) => void) | null) {
    this._reloadHandler = handler;
  }

  /**
   * Begin polling `getChat(chatId)` with exponential back-off.
   *
   * While `chat.status === "generating"` polling continues.
   * Completion is detected when `chat.status` transitions to `null`.
   * Errors (`chat.status === "error"`) and timeouts (5 min) are
   * surfaced to subscribers.
   */
  startPolling(
    chatId: string,
    getChat: (id: string) => Promise<ChatOut>,
    onStreamEnd?: (chatId: string) => void,
  ) {
    this.cancelPolling();
    this.pendingOnStreamEnd = onStreamEnd ?? null;

    const controller = new AbortController();
    this.pollAbortController = controller;
    const { signal } = controller;

    const startedAt = Date.now();
    this.current = { chatId, status: "polling", startedAt };
    this.notify();

    void this.poll(chatId, getChat, signal, startedAt);
  }

  /**
   * When `getChat` already shows completion (e.g. user returned via deep link while
   * the poll loop was stalled or `onStreamEnd` could not run because the runtime
   * unmounted), finish the same way as a successful poll.
   */
  finishPollingSuccess(chatId: string) {
    if (this.current?.chatId !== chatId || this.current?.status !== "polling") {
      return;
    }
    const startedAt = this.current.startedAt;
    this.cancelPolling();
    this.complete(chatId, startedAt);
  }

  /** Cancel any active polling and clear state (REQ-10). */
  onNewStream() {
    this.cancelPolling();
    this.pendingOnStreamEnd = null;
    if (this.current) {
      this.current = null;
      this.notify();
    }
  }

  /** Dismiss the current indicator. */
  clear() {
    this.cancelPolling();
    this.pendingOnStreamEnd = null;
    if (this.current) {
      this.current = null;
      this.notify();
    }
  }

  getSnapshot = (): BackgroundGenerationState | null => this.current;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  // ── Private ────────────────────────────────────────────────────

  private async poll(
    chatId: string,
    getChat: (id: string) => Promise<ChatOut>,
    signal: AbortSignal,
    startedAt: number,
  ) {
    let attempt = 0;

    while (!signal.aborted) {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        this.current = { chatId, status: "timeout", startedAt };
        this.pendingOnStreamEnd = null;
        this.notify();
        return;
      }

      const delay =
        POLL_INTERVALS_MS[Math.min(attempt, POLL_INTERVALS_MS.length - 1)];
      await new Promise<void>((r) => setTimeout(r, delay));
      if (signal.aborted) return;

      try {
        const chat = await getChat(chatId);
        if (signal.aborted) return;

        if (chat.status === "error") {
          this.current = { chatId, status: "error", startedAt };
          this.pendingOnStreamEnd = null;
          this.notify();
          return;
        }

        // Per spec (REQ-7): status === null means generation completed.
        // While the background task is running, status is non-null.
        if (chat.status == null) {
          this.complete(chatId, startedAt);
          return;
        }
      } catch {
        // Network error during polling — keep trying
      }

      attempt++;
    }
  }

  private complete(chatId: string, startedAt: number) {
    if (this.current?.chatId !== chatId) return;
    if (this.current?.status === "completed") return;

    const onStreamEnd = this.pendingOnStreamEnd;
    this.pendingOnStreamEnd = null;

    this.current = { chatId, status: "completed", startedAt };
    this.notify();
    onStreamEnd?.(chatId);
    this._reloadHandler?.(chatId);

    // Auto-dismiss the toast after the reload has had time to process.
    setTimeout(() => {
      if (
        this.current?.status === "completed" &&
        this.current?.chatId === chatId
      ) {
        this.current = null;
        this.notify();
      }
    }, 4000);
  }

  private cancelPolling() {
    this.pollAbortController?.abort();
    this.pollAbortController = null;
  }

  private notify() {
    this.listeners.forEach((l) => l());
  }
}

export const backgroundGenerationStore = new BackgroundGenerationStore();
